import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ExternalLinkIcon } from 'lucide-react';
import { Campaign } from '../data/mockData';
ChartJS.register(ArcElement, Tooltip, Legend);
interface CampaignCardProps {
  campaign: Campaign;
}
export function CampaignCard({
  campaign
}: CampaignCardProps) {
  const chartData = {
    labels: campaign.channels.map(c => c.name),
    datasets: [{
      data: campaign.channels.map(c => c.value),
      backgroundColor: campaign.channels.map(c => c.color),
      borderWidth: 0,
      hoverOffset: 4
    }]
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 16,
          font: {
            family: 'system-ui, -apple-system, sans-serif',
            size: 12
          },
          generateLabels: (chart: any) => {
            const data = chart.data;
            return data.labels.map((label: string, i: number) => ({
              text: `${label} ${data.datasets[0].data[i]}%`,
              fillStyle: data.datasets[0].backgroundColor[i],
              strokeStyle: 'transparent',
              pointStyle: 'circle',
              hidden: false,
              index: i
            }));
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function (context: any) {
            return ` ${context.label}: ${context.raw}%`;
          }
        }
      }
    }
  };
  const accentColorClass = campaign.type === 'Brand' ? 'before:bg-slate-800' : 'before:bg-red-500';
  return <div className={`relative bg-card border border-border rounded-2xl p-6 pl-8 shadow-sm flex flex-col gap-6 before:absolute before:left-0 before:top-6 before:bottom-6 before:w-1.5 before:rounded-r-full ${accentColorClass} overflow-hidden`} data-id="element-11">
      {/* Header */}
      <div data-id="element-12">
        <div className="flex items-start justify-between mb-2" data-id="element-13">
          <h4 className="text-lg font-semibold text-foreground leading-tight" data-id="element-14">
            {campaign.title}
          </h4>
          <div className="flex items-center gap-2 shrink-0 ml-3" data-id="element-15">
            <a href={campaign.googleSheetUrl || `https://docs.google.com/spreadsheets/d/example-${campaign.id}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="Open in Google Sheets" data-id="element-16">
              <ExternalLinkIcon className="w-4 h-4" data-id="element-17" />
            </a>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${campaign.type === 'Brand' ? 'bg-slate-800 text-white' : 'bg-red-500 text-white'}`} data-id="element-18">
              {campaign.type}
            </span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground" data-id="element-19">
          {campaign.date} • {campaign.model}
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="flex flex-col gap-4" data-id="element-20">
        {/* Row 1: 4 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-4" data-id="element-21">
          <div className="flex flex-col" data-id="element-22">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-23">Budget</span>
            <span className="text-sm font-semibold text-foreground" data-id="element-24">
              {campaign.metrics.budget}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-25">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-26">
              Impressions
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-27">
              {campaign.metrics.impressions}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-28">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-29">Clicks</span>
            <span className="text-sm font-semibold text-foreground" data-id="element-30">
              {campaign.metrics.clicks}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-31">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-32">Leads</span>
            <span className="text-sm font-semibold text-foreground" data-id="element-33">
              {campaign.metrics.leads}
            </span>
          </div>
        </div>

        {/* Row 2: 4 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-4 pt-2 border-t border-border/50" data-id="element-34">
          <div className="flex flex-col" data-id="element-35">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-36">
              Time on site
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-37">
              {campaign.metrics.timeOnSite}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-38">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-39">
              Bounce rate
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-40">
              {campaign.metrics.bounceRate}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-41">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-42">
              Cost per click
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-43">
              €
              {campaign.metrics.rawClicks > 0 ? (campaign.metrics.rawBudget / campaign.metrics.rawClicks).toFixed(2) : '0.00'}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-44">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-45">
              Cost per lead
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-46">
              {campaign.metrics.costPerLead}
            </span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="pt-4 border-t border-border mt-auto" data-id="element-47">
        <span className="text-xs font-medium text-muted-foreground block mb-4" data-id="element-48">
          Channel Distribution
        </span>
        <div className="h-[160px] w-full" data-id="element-49">
          <Doughnut data={chartData} options={chartOptions} data-id="element-50" />
        </div>
      </div>
    </div>;
}